/**
 * API Service with Automatic Token Refresh
 *
 * Features:
 * - Automatic silent token refresh on 401
 * - Request queuing during refresh
 * - Consistent token management
 */

export const API_BASE = import.meta.env.VITE_API_URL || 'https://reeva-api.returned.com'
export const AUTH_BASE = import.meta.env.VITE_AUTH_URL || 'https://identity.returned.com'

function deriveWsBase(httpBase: string): string | null {
  try {
    const u = new URL(httpBase)
    if (u.protocol === 'https:') u.protocol = 'wss:'
    else if (u.protocol === 'http:') u.protocol = 'ws:'
    else return null
    return u.origin
  } catch {
    return null
  }
}

/**
 * WebSocket base URL.
 *
 * Prefer explicit `VITE_API_WS_URL` when provided, otherwise derive from `API_BASE`.
 * This keeps REST + WS in the same environment (prod/staging/local) by default.
 */
export const WS_BASE = (() => {
  const explicit = (import.meta.env.VITE_API_WS_URL || '').trim()
  if (explicit) return explicit.replace(/\/+$/, '')
  return (deriveWsBase(API_BASE) || 'wss://returned.com').replace(/\/+$/, '')
})()

// Token keys (consolidated)
const ACCESS_TOKEN_KEY = 'reeva_auth_token'
const REFRESH_TOKEN_KEY = 'reeva_refresh_token'

// Refresh state management
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

/**
 * Get the current access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

/**
 * Get the refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * Store tokens after auth
 */
export function storeTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

/**
 * Clear all tokens (logout)
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('reeva_user')
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return null
  }

  try {
    const response = await fetch(`${AUTH_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      // Refresh failed - clear tokens and redirect to login
      clearTokens()
      return null
    }

    const data = await response.json()

    // Store new tokens
    storeTokens(data.access_token, data.refresh_token)

    return data.access_token
  } catch (error) {
    console.error('[API] Token refresh error:', error)
    clearTokens()
    return null
  }
}

/**
 * Handle token refresh with request queuing
 */
async function handleTokenRefresh(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = refreshAccessToken()

  try {
    const token = await refreshPromise
    return token
  } finally {
    isRefreshing = false
    refreshPromise = null
  }
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function apiFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let response = await apiRaw(endpoint, options)

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new ApiError(errorText, response.status)
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return {} as T
  }

  return JSON.parse(text)
}

function isBodyFormData(body: BodyInit | null | undefined): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

function isBinaryBody(body: BodyInit | null | undefined): boolean {
  if (!body) return false
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true
  if (body instanceof ArrayBuffer) return true
  if (ArrayBuffer.isView(body)) return true
  return false
}

/**
 * Raw response helper with token refresh/retry semantics.
 */
export async function apiRaw(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`

  const makeRequest = async (authToken: string | null): Promise<Response> => {
    const headers = new Headers(options.headers || {})

    if (authToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${authToken}`)
    }

    // Only default JSON content-type for plain string request bodies.
    if (
      options.body &&
      typeof options.body === 'string' &&
      !headers.has('Content-Type') &&
      !isBodyFormData(options.body) &&
      !isBinaryBody(options.body)
    ) {
      headers.set('Content-Type', 'application/json')
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000)

    const onAbort = () => controller.abort()
    if (options.signal) {
      if (options.signal.aborted) {
        onAbort()
      } else {
        options.signal.addEventListener('abort', onAbort, { once: true })
      }
    }

    try {
      return await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      })
    } finally {
      clearTimeout(timeoutId)
      if (options.signal && !options.signal.aborted) {
        options.signal.removeEventListener('abort', onAbort)
      }
    }
  }

  let response = await makeRequest(getAccessToken())

  if (response.status === 401) {
    const newToken = await handleTokenRefresh()
    if (!newToken) {
      throw new AuthError('Session expired. Please log in again.')
    }
    response = await makeRequest(newToken)
  }

  return response
}

export async function apiText(endpoint: string, options: RequestInit = {}): Promise<string> {
  const response = await apiRaw(endpoint, options)
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new ApiError(errorText, response.status)
  }
  return response.text()
}

export async function apiBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
  const response = await apiRaw(endpoint, options)
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new ApiError(errorText, response.status)
  }
  return response.blob()
}

/**
 * Custom error classes
 */
export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthError'
  }
}

/**
 * Convenience methods
 */
export const api = {
  get: <T = unknown>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  put: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  patch: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  delete: <T = unknown>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),

  raw: (endpoint: string, options?: RequestInit) => apiRaw(endpoint, options),
  text: (endpoint: string, options?: RequestInit) => apiText(endpoint, options),
  blob: (endpoint: string, options?: RequestInit) => apiBlob(endpoint, options),
}

/**
 * Identity API — reuses apiFetch pipeline but targets AUTH_BASE.
 * For passkey, QR auth, and other identity-api endpoints.
 */
function identityEndpoint(endpoint: string): string {
  if (endpoint.startsWith('http')) return endpoint
  return `${AUTH_BASE}${endpoint}`
}

export const identityApi = {
  get: <T = unknown>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(identityEndpoint(endpoint), { ...options, method: 'GET' }),

  post: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(identityEndpoint(endpoint), {
      ...options,
      method: 'POST',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  delete: <T = unknown>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(identityEndpoint(endpoint), { ...options, method: 'DELETE' }),
}

export default api
